import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Typography, showToast } from '@attentive-platform/stem-ui';

import { fetchCompanyLookup } from '../../ScopeConsole/api';
import { SOP_MODE_LABELS } from '../../../Constants/sopMode';
import { formatLocaleDate, isValidEmail } from '../../../Utils/HelperFunctions';
import { LookupModalStyles as S } from './styles';

const LookupModal = ({ show, onClose, onPick, initialQuery = '', replacing = false }) => {
  const [query, setQuery] = useState(initialQuery);
  const [result, setResult] = useState(null);
  const [prospectOnly, setProspectOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    setQuery(initialQuery);
    setResult(null);
    setProspectOnly(false);
  }, [show, initialQuery]);

  useEffect(() => {
    if (!show) return undefined;
    const email = query.trim();
    if (!email || !isValidEmail(email)) {
      setResult(null);
      setProspectOnly(false);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const id = setTimeout(async () => {
      setLoading(true);
      const { data, error: err } = await fetchCompanyLookup({ email, signal: controller.signal });
      if (controller.signal.aborted) return;
      setLoading(false);
      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || err.error || 'Lookup failed';
        showToast(msg, 'error');
        setResult(null);
        setProspectOnly(false);
        return;
      }
      const cardId = data?.card_id ?? data?.company?.card_id ?? null;
      // Match on card_id (scope record), not on company payload.
      if (cardId) {
        setResult({
          company: data.company || null,
          scope_mode: data.scope_mode,
          card_id: cardId,
          customer_email: data.company?.customer_email || data.customer_email || email
        });
        setProspectOnly(false);
      } else {
        setResult(null);
        setProspectOnly(!!data?.prospect_only);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [query, show]);

  const trimmed = query.trim();
  const isEmailShape = trimmed && isValidEmail(trimmed);

  return (
    <Modal
      show={show}
      onClose={onClose}
      heading={<span className='pl-4'>{replacing ? 'Replace scope record' : 'Link scope record'}</span>}
      maxWidth='lg'
      content={
        <S.ContentWrap>
          <Typography variant='body3' color='#666666'>
            {replacing
              ? 'Find a different submitted scope record to become the new scope of record.'
              : 'Find the scope submission that belongs to this company.'}
          </Typography>
          <Input
            size='large'
            placeholder='Customer email'
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <Typography variant='body4' color='#808080'>
            {loading
              ? 'Looking up…'
              : result
                ? '1 scope record matched — linked by card id from this customer email'
                : trimmed && !isEmailShape
                  ? 'Enter a full customer email to look up the scope record'
                  : 'Lookup returns a scope card id for the customer email on file'}
          </Typography>
          <S.Results>
            {result && (
              <S.RowFirst onClick={() => onPick && onPick(result)}>
                <S.ColTrunc>
                  <S.CompanyText>
                    {result.company?.name || result.customer_email || 'Scope record'}
                  </S.CompanyText>
                  <S.EmailText>
                    {result.customer_email || '—'}
                    {result.card_id ? ` · card ${result.card_id}` : ''}
                  </S.EmailText>
                </S.ColTrunc>
                <S.Col>
                  <S.UpperLabel>Registered</S.UpperLabel>
                  <Typography variant='body3' sx={{ fontWeight: 500 }}>
                    {formatLocaleDate(result.company?.registered_on) || '—'}
                  </Typography>
                </S.Col>
                <S.Col>
                  <S.UpperLabel>Scope mode</S.UpperLabel>
                  <Typography variant='body3' sx={{ fontWeight: 500 }}>
                    {SOP_MODE_LABELS[result.scope_mode] || result.scope_mode || '—'}
                  </Typography>
                </S.Col>
              </S.RowFirst>
            )}
            {!loading && !result && prospectOnly && (
              <Typography variant='body4' color='#9E9E9E' sx={{ paddingTop: '2px' }}>
                No scope card for this email yet — the customer is still a prospect.
              </Typography>
            )}
            {!loading && !result && isEmailShape && !prospectOnly && (
              <Typography variant='body4' color='#9E9E9E' sx={{ paddingTop: '2px' }}>
                No card id found for this email.
              </Typography>
            )}
          </S.Results>
          <S.Actions>
            <Button variant='outlined' color='primary' size='medium' onClick={onClose}>
              Cancel
            </Button>
          </S.Actions>
        </S.ContentWrap>
      }
    />
  );
};

export default LookupModal;
